import React, { useMemo } from 'react';
import { CopyIcon, VisaLogoIcon } from 'assets/icons';

interface CardDetailsProps {
  cardHolderName: string;
  cardNumberLast4: string;
  showDetails: boolean;
  contentTextColor: string;
  cardNumberDotColor: string;
  getElementHeight: (height: number) => number;
  getElementWidth: (width: number) => number;
}

const CardDetails: React.FC<CardDetailsProps> = ({
  cardHolderName,
  cardNumberLast4,
  showDetails,
  contentTextColor,
  cardNumberDotColor,
  getElementHeight,
  getElementWidth
}) => {
  const createDot = (dotSize: number) => (
    <div
      className={`rounded-full ${cardNumberDotColor}`}
      style={{
        height: getElementHeight(dotSize),
        width: getElementWidth(dotSize)
      }}
    />
  );

  const cardNumberDots = useMemo(
    () =>
      Array.from({ length: 3 }, (_, groupIndex) => (
        <div key={groupIndex} className="flex items-center gap-[7px]">
          {Array.from({ length: 4 }, (__, dotIndex) => (
            <div key={dotIndex}>{createDot(7)}</div>
          ))}
        </div>
      )),
    [cardNumberDotColor, getElementHeight, getElementWidth]
  );

  const expiryDots = useMemo(
    () => Array.from({ length: 2 }, (_, groupIndex) => <div key={groupIndex}>{createDot(4)}</div>),
    [cardNumberDotColor, getElementHeight, getElementWidth]
  );

  const cvvDots = useMemo(
    () => Array.from({ length: 3 }, (_, dotIndex) => <div key={dotIndex}>{createDot(4)}</div>),
    [cardNumberDotColor, getElementHeight, getElementWidth]
  );
  return (
    <>
      {/* Card Number Section */}
      <div className="flex items-center">
        <div className={`flex items-center ${showDetails ? 'flex' : 'hidden'}`}>
          <span className="w-[210px] font-medium tracking-widest" id="card-number-display" aria-label="Card number" />

          <div className="relative flex items-center">
            <CopyIcon className={`path-fill-current h-4 w-4 ${contentTextColor}`} aria-hidden="true" />
            <span
              id="card-number-copy"
              className="absolute right-0 h-5 w-5 cursor-pointer"
              role="button"
              tabIndex={showDetails ? 0 : -1}
              aria-label="Copy card number to clipboard"
            />
          </div>
        </div>

        {!showDetails && (
          <div className="flex items-center gap-3">
            {cardNumberDots}
            <span className={`text-xl font-bold ${contentTextColor}`}>{cardNumberLast4}</span>
          </div>
        )}
      </div>

      {/* Cardholder Name Section */}
      <div>
        <span className={`font-lato text-17px font-medium capitalize ${contentTextColor}`} aria-label="Cardholder name">
          {cardHolderName}
        </span>
      </div>

      {/* Expiry, CVV, and Logo Section */}
      <div className="flex w-full items-end gap-5">
        {/* Expiry Date */}
        <div className={`flex flex-col items-start text-px13 font-medium ${contentTextColor}`}>
          <span aria-label="Expiry date">Expiry</span>
          <div className={`flex items-center ${showDetails ? 'flex gap-2' : 'hidden'}`}>
            <span id="card-expiry-display" className="w-10 font-lato text-sm" />
            <div className="relative flex items-center">
              <CopyIcon className={`path-fill-current h-4 w-4 ${contentTextColor}`} aria-hidden="true" />
              <span
                id="card-expiry-copy"
                className="absolute right-0 h-5 w-5 cursor-pointer"
                role="button"
                tabIndex={showDetails ? 0 : -1}
                aria-label="Copy card expiry to clipboard"
              />
            </div>
          </div>
          {!showDetails && (
            <div className="flex items-center gap-1">
              {expiryDots}
              <span className={`text-sm ${contentTextColor}`}>/</span>
              {expiryDots}
            </div>
          )}
        </div>

        {/* CVV */}
        <div className={`flex flex-col items-start text-px13 font-medium ${contentTextColor}`}>
          <span aria-label="Card verification value">CVV</span>
          <div className={`flex items-center ${showDetails ? 'flex gap-2' : 'hidden'}`}>
            <span id="card-cvc-display" className="w-7 font-lato text-sm" />
            <div className="relative flex items-center">
              <CopyIcon className={`path-fill-current h-4 w-4 ${contentTextColor}`} aria-hidden="true" />
              <span
                id="card-cvc-copy"
                className="absolute right-0 h-5 w-5 cursor-pointer"
                role="button"
                tabIndex={showDetails ? 0 : -1}
                aria-label="Copy card cvc to clipboard"
              />
            </div>
          </div>
          {!showDetails && <div className="flex items-center gap-[7px] py-2">{cvvDots}</div>}
        </div>

        {/* Visa Logo */}
        <div className="ml-auto flex justify-end" aria-hidden="true">
          <VisaLogoIcon
            className={`${contentTextColor} path-fill-current`}
            style={{
              height: getElementHeight(15),
              width: getElementWidth(46)
            }}
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(CardDetails);
